// SCREENS-VIEWER: the Screens workspace's left sidebar. Sessions grouped Needs you · Live ·
// Waiting; a row: the DE / AG / person, the kind icon, the task and the account, its state.
// Admins open a bare desktop or browser from the icons at the top (hidden for everyone else).
import { Badge, cn, InfoTip, Spinner, WorkerBadge, workerName } from "@companyos/ui";
import { ArrowUpToLineIcon, GlobeIcon, MonitorIcon } from "lucide-react";
import type { ReactNode } from "react";
import { HOVER_STRIP, IconAction } from "../cadence/cadence-parts";
import { leaseWhat } from "./hand-back-dialog";
import type { Lease, OperatorScreen, SessionGroups, Stack } from "./screens-queries";
import type { OpenKind } from "./session-actions";

/** A session being opened (the request is in flight): a row with a spinner. */
export type PendingOpen = { key: string; kind: OpenKind; label: string; since: number };

const KindIcon = ({ kind }: { kind: Lease["kind"] }) =>
  kind === "desktop" ? (
    <MonitorIcon aria-label="Desktop" className="size-3.5 shrink-0 text-muted-foreground" />
  ) : (
    <GlobeIcon aria-label="Browser" className="size-3.5 shrink-0 text-muted-foreground" />
  );

export function stateOf(l: Lease, me: string | null): { text: string; tone: string } {
  if (l.state === "queued") return { text: `#${l.position ?? "?"}`, tone: "bg-muted-foreground" };
  if (l.controller)
    return {
      text: l.controller.membership_id === me ? "You" : l.controller.name,
      tone: "bg-warning-text",
    };
  if (l.state === "needs_you") return { text: "Needs you", tone: "bg-warning-text" };
  if (l.operator_control) return { text: "Operator", tone: "bg-warning-text" };
  return { text: "Live", tone: "bg-success-text" };
}

export function SessionList({
  groups,
  operator,
  pending,
  selected,
  me,
  admin,
  canBump,
  stack,
  opening,
  onSelect,
  onSelectPending,
  onHelp,
  onOpen,
  onBump,
}: {
  groups: SessionGroups;
  operator: OperatorScreen[];
  pending: PendingOpen[];
  /** The selected lease id, or a pending key. */
  selected: string | null;
  me: string | null;
  admin: boolean;
  canBump: boolean;
  stack: Stack;
  opening: boolean;
  onSelect: (l: Lease) => void;
  onSelectPending: (key: string) => void;
  onHelp: (l: Lease) => void;
  onOpen: (kind: OpenKind) => void;
  onBump: (l: Lease) => void;
}) {
  const nothing =
    !groups.needsYou.length &&
    !groups.live.length &&
    !groups.waiting.length &&
    !operator.length &&
    !pending.length;
  return (
    <aside
      aria-label="Sessions"
      data-slot="session-list"
      className="flex min-h-0 w-72 shrink-0 flex-col border-r border-border max-md:max-h-64 max-md:w-full max-md:border-r-0 max-md:border-b"
    >
      <div className="flex h-10 shrink-0 items-center gap-1 px-3">
        <span className="flex-1 truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Sessions
        </span>
        {admin ? <OpenActions stack={stack} opening={opening} onOpen={onOpen} /> : null}
      </div>
      <nav className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-2">
        {nothing ? (
          <p className="px-3 py-2 text-sm text-muted-foreground" data-slot="no-sessions">
            No sessions
          </p>
        ) : null}
        {groups.needsYou.length ? (
          <Group label="Needs you" slot="group-needs-you">
            {groups.needsYou.map((l) => (
              <Row
                key={l.id}
                lease={l}
                me={me}
                selected={selected === l.id}
                onSelect={() => onSelect(l)}
                action={
                  l.can.control && !l.controller ? (
                    <button
                      type="button"
                      className="shrink-0 rounded-sm bg-warning-soft px-1.5 text-xs font-medium text-warning-text hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        onHelp(l);
                      }}
                    >
                      {l.needs_you?.why === "login" ? "Log in" : "Help"}
                    </button>
                  ) : null
                }
              />
            ))}
          </Group>
        ) : null}
        {groups.live.length || pending.length || operator.length ? (
          <Group label="Live" slot="group-live">
            {pending.map((p) => (
              <li key={p.key}>
                <button
                  type="button"
                  data-slot="session-row"
                  data-pending=""
                  aria-current={selected === p.key ? "true" : undefined}
                  onClick={() => onSelectPending(p.key)}
                  className={rowClass(selected === p.key)}
                >
                  <Spinner className="size-3.5 shrink-0" aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate">{p.label}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">Starting</span>
                </button>
              </li>
            ))}
            {groups.live.map((l) => (
              <Row
                key={l.id}
                lease={l}
                me={me}
                selected={selected === l.id}
                onSelect={() => onSelect(l)}
              />
            ))}
            {operator.map((o) => (
              <li
                key={o.lease_id}
                data-slot="operator-row"
                className="flex h-8 min-w-0 items-center gap-2 overflow-hidden px-3 text-sm text-muted-foreground"
              >
                <KindIcon kind={o.kind} />
                <span className="min-w-0 flex-1 truncate">{o.name}</span>
                <Badge variant="neutral" className="h-4 px-1 text-[10px]">
                  Operator
                </Badge>
              </li>
            ))}
          </Group>
        ) : null}
        {groups.waiting.length ? (
          <Group
            label="Waiting"
            slot="group-waiting"
            info="Sessions wait when every browser or desktop is in use. People go first."
          >
            {groups.waiting.map((l) => (
              <Row
                key={l.id}
                lease={l}
                me={me}
                selected={selected === l.id}
                onSelect={() => onSelect(l)}
                action={
                  canBump && l.can.bump ? (
                    <div className={HOVER_STRIP}>
                      <IconAction
                        label={`Bump ${workerName(l.holder.name)}`}
                        tip="Bump to the front"
                        onClick={() => onBump(l)}
                      >
                        <ArrowUpToLineIcon />
                      </IconAction>
                    </div>
                  ) : null
                }
              />
            ))}
          </Group>
        ) : null}
      </nav>
    </aside>
  );
}

function OpenActions({
  stack,
  opening,
  onOpen,
}: {
  stack: Stack;
  opening: boolean;
  onOpen: (kind: OpenKind) => void;
}) {
  const none = (kind: OpenKind) =>
    (kind === "desktop" ? stack.capacity.desktops_max : stack.capacity.browsers_max) <= 0;
  return (
    <span className="inline-flex items-center" data-slot="open-actions">
      <IconAction
        label="Open a desktop"
        tip={none("desktop") ? "No desktops on this computer" : "Open a desktop"}
        disabled={opening || none("desktop")}
        onClick={() => onOpen("desktop")}
      >
        <MonitorIcon />
      </IconAction>
      <IconAction
        label="Open a browser"
        tip={none("browser") ? "No browsers on this computer" : "Open a browser"}
        disabled={opening || none("browser")}
        onClick={() => onOpen("browser")}
      >
        <GlobeIcon />
      </IconAction>
      {none("desktop") ? (
        <span data-slot="no-desktops" className="inline-flex">
          <InfoTip label="About desktops">
            This company's computer has no desktops. Your operator can add them.
          </InfoTip>
        </span>
      ) : null}
    </span>
  );
}

function Group({
  label,
  slot,
  info,
  children,
}: {
  label: string;
  slot: string;
  info?: string;
  children: ReactNode;
}) {
  return (
    <section aria-label={label} data-slot={slot} className="pt-2">
      <h3 className="flex h-6 items-center gap-1 px-3 text-xs font-medium text-muted-foreground">
        {label}
        {info ? <InfoTip label={`About ${label.toLowerCase()}`}>{info}</InfoTip> : null}
      </h3>
      <ul className="grid">{children}</ul>
    </section>
  );
}

const rowClass = (selected: boolean) =>
  cn(
    "flex h-8 w-full min-w-0 items-center gap-2 overflow-hidden px-3 text-left text-sm hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring",
    selected && "bg-primary-soft text-primary-text hover:bg-primary-soft",
  );

function Row({
  lease,
  me,
  selected,
  onSelect,
  action,
}: {
  lease: Lease;
  me: string | null;
  selected: boolean;
  onSelect: () => void;
  action?: ReactNode;
}) {
  const name = workerName(lease.holder.name);
  const st = stateOf(lease, me);
  const detail = [lease.task?.title, leaseWhat(lease)].filter(Boolean).join(" · ");
  return (
    <li className="group/row relative flex min-w-0 items-center" data-slot="session-item">
      <button
        type="button"
        data-slot="session-row"
        data-lease={lease.id}
        data-state={lease.state}
        aria-current={selected ? "true" : undefined}
        title={`${name} · ${detail}`}
        onClick={onSelect}
        className={cn(rowClass(selected), "h-auto min-h-8 flex-col items-stretch gap-0 py-1")}
      >
        <span className="flex min-w-0 items-center gap-2 overflow-hidden">
          <WorkerBadge kind={lease.holder.kind} size="sm" />
          <span className="min-w-0 flex-1 truncate font-medium">{name}</span>
          <KindIcon kind={lease.kind} />
          {lease.state === "needs_you" && !lease.controller ? (
            // SCREEN-HANDOFF: a session waiting for a person stands out.
            <Badge variant="warning" dot data-slot="row-state" data-needs-you="">
              Needs you
            </Badge>
          ) : (
            <span
              className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground"
              data-slot="row-state"
            >
              <span aria-hidden="true" className={cn("size-1.5 rounded-full", st.tone)} />
              {st.text}
            </span>
          )}
        </span>
        <span
          className={cn("truncate pl-6 text-xs text-muted-foreground", action && "pr-14")}
          data-slot="row-detail"
        >
          {detail}
        </span>
      </button>
      {action ? <span className="absolute right-2 bottom-0.5">{action}</span> : null}
    </li>
  );
}
